import { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertCircle, Loader2, PartyPopper, MapPin } from 'lucide-react';
import { Button } from '../Common';
import { csvImportAPI } from '../../api/endpoints';
import { useBackgroundTask } from '../../hooks/useBackgroundTask';
import type { CSVImportResponse } from '../../types';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

interface ImportResultStepProps {
  sessionId: string;
  rowNumbersToImport: number[];
  onComplete: () => void;
  embedded?: boolean;
}

export function ImportResultStep({
  sessionId,
  rowNumbersToImport,
  onComplete,
  embedded = false,
}: ImportResultStepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<CSVImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasStartedImport = useRef(false);

  const { isActive, task, startPolling } = useBackgroundTask({
    label: 'CSV import',
    onComplete: (t) => {
      if (t.result) {
        setResult(t.result as unknown as CSVImportResponse);
        queryClient.invalidateQueries({ queryKey: ['venues'] });
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
      }
    },
    onError: (t) => {
      setError(t.error || 'Import failed');
    },
  });

  useEffect(() => {
    if (hasStartedImport.current) return;
    hasStartedImport.current = true;

    csvImportAPI.import({
      session_id: sessionId,
      row_numbers_to_import: rowNumbersToImport,
    }).then((response) => {
      startPolling(response.data.task_id);
    }).catch((err: any) => {
      setError(err.response?.data?.detail || 'Failed to start import');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isActive || (!result && !error)) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-12 h-12 text-dark-accent animate-spin mb-4" />
        <p className="text-lg text-dark-text-primary mb-2">Importing data...</p>
        <p className="text-dark-text-secondary">
          {task?.message || `Creating ${rowNumbersToImport.length} records`}
        </p>
        {task && task.progress > 0 && (
          <div className="w-64 mt-4 bg-dark-elevated rounded-full h-2">
            <div
              className="bg-dark-accent h-2 rounded-full transition-all"
              style={{ width: `${Math.round(task.progress)}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h3 className="text-xl font-medium text-dark-text-primary mb-2">
            Import Failed
          </h3>
          <p className="text-dark-text-secondary text-center max-w-md">
            {error}
          </p>
        </div>

        <div className="flex justify-center">
          <Button onClick={onComplete}>Close</Button>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const hasErrors = result.errors.length > 0;
  const contactsLinked = result.contacts_linked ?? 0;
  const totalCreated = result.venues_created + result.contacts_created + contactsLinked;

  return (
    <div className="space-y-6">
      {/* Success message */}
      <div className="flex flex-col items-center justify-center py-8">
        {totalCreated > 0 ? (
          <>
            <PartyPopper className="w-16 h-16 text-green-400 mb-4" />
            <h3 className="text-xl font-medium text-dark-text-primary mb-2">
              Import Complete!
            </h3>
          </>
        ) : (
          <>
            <AlertCircle className="w-16 h-16 text-yellow-400 mb-4" />
            <h3 className="text-xl font-medium text-dark-text-primary mb-2">
              No Records Created
            </h3>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-6">
        <div className="text-center px-6 py-4 bg-dark-elevated rounded-lg">
          <p className="text-3xl font-bold text-green-400 mb-1">
            {result.venues_created}
          </p>
          <p className="text-dark-text-secondary">
            Venue{result.venues_created !== 1 ? 's' : ''} created
          </p>
        </div>
        <div className="text-center px-6 py-4 bg-dark-elevated rounded-lg">
          <p className="text-3xl font-bold text-blue-400 mb-1">
            {result.contacts_created}
          </p>
          <p className="text-dark-text-secondary">
            Contact{result.contacts_created !== 1 ? 's' : ''} created
          </p>
        </div>
        {contactsLinked > 0 && (
          <div
            className="text-center px-6 py-4 bg-dark-elevated rounded-lg"
            title="Addresses you already had a contact for: that contact was linked to the new venues instead of duplicated."
          >
            <p className="text-3xl font-bold text-blue-300 mb-1">
              {contactsLinked}
            </p>
            <p className="text-dark-text-secondary">
              Existing contact{contactsLinked !== 1 ? 's' : ''} linked
            </p>
          </div>
        )}
      </div>

      {/* Errors (if any) */}
      {hasErrors && (
        <div className="space-y-3">
          <h4 className="font-medium text-dark-text-primary flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            Some rows had errors ({result.errors.length})
          </h4>
          <div className="max-h-40 overflow-y-auto bg-dark-elevated rounded-lg p-3">
            {result.errors.map((err, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 py-1 text-sm border-b border-dark-border/50 last:border-0"
              >
                <span className="text-dark-text-secondary">Row {err.row_number}:</span>
                <span className="text-red-400">{err.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success indicator */}
      {totalCreated > 0 && !hasErrors && (
        <div className="flex items-center justify-center gap-2 text-green-400">
          <CheckCircle className="w-5 h-5" />
          <span>All records imported successfully</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center gap-3 pt-4">
        {embedded ? (
          <>
            <Button onClick={() => window.location.reload()}>Import More</Button>
            <Button variant="secondary" onClick={() => navigate('/venues')}>
              <MapPin className="w-4 h-4" />
              Explore Venues
            </Button>
          </>
        ) : (
          <Button onClick={onComplete}>Done</Button>
        )}
      </div>
    </div>
  );
}
